import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { AppProviders } from "../providers/AppProviders";
import "../styles/globals.css";
import "../commands/commands"; // Import commands to register them

import { initializeTracking } from "../utils/analytics.utils";
import { installGlobalErrorHandlers, reportError } from "../utils/error-reporting";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { TOAST_STORAGE_KEY } from "../config/storage.config";
import { toast } from "sonner";
import { debugLog } from "../utils/debug";

/* global document, Office, module, require, HTMLElement */

interface ToastMessage {
  type: "info" | "error" | "success" | "warning";
  message: string;
  timestamp?: number;
}

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

function checkForToastMessages(): void {
  try {
    const stored = sessionStorage.getItem(TOAST_STORAGE_KEY);
    if (stored != null) {
      const parsed: unknown = JSON.parse(stored);
      if (
        typeof parsed !== "object" ||
        parsed == null ||
        !("type" in parsed) ||
        !("message" in parsed)
      )
        return;
      const message = parsed as ToastMessage;

      // Always clear regardless of whether we show it
      sessionStorage.removeItem(TOAST_STORAGE_KEY);

      // Only show if recent (within last 10 seconds)
      if (message.timestamp == null || Date.now() - message.timestamp < 10000) {
        switch (message.type) {
          case "error":
            toast.error(message.message);
            break;
          case "warning":
            toast.warning(message.message);
            break;
          case "info":
          case "success":
            toast(message.message);
            break;
          default:
            toast(message.message);
        }
      }
    }
  } catch (_e) {
    // Ignore errors
  }
}

/* Render application after Office initializes */
Office.onReady(() => {
  debugLog("boot", "Office.onReady", {
    appUrl: process.env.APP_URL ?? "",
    apiBase: process.env.API_BASE_URL ?? "",
    appEnv: process.env.APP_ENV ?? "",
  });
  // FIRST, before anything that could throw. Catches errors thrown outside
  // React (Office.js callbacks, timers, rejected promises) that an error
  // boundary cannot see and that are otherwise invisible in a task pane.
  installGlobalErrorHandlers();

  // Analytics must never be able to stop the app mounting. It only appends
  // script tags today, but having the whole resilience stack depend on an
  // unrelated unguarded call is exactly the "blank pane, nothing captured"
  // failure this work exists to prevent.
  try {
    initializeTracking();
  } catch (error) {
    reportError(error, { area: "analytics-init" });
  }

  root?.render(
    <ErrorBoundary area="taskpane-root" title="Quillio couldn't start">
      <AppProviders>
        <App />
      </AppProviders>
    </ErrorBoundary>
  );

  // Check for toast messages from ribbon commands (cross-runtime via sessionStorage).
  // Runs continuously since commands can be triggered at any time.
  checkForToastMessages();
  const toastPollId = setInterval(checkForToastMessages, 500);
  window.addEventListener("beforeunload", () => {
    clearInterval(toastPollId);
  });
});

if (module.hot) {
  module.hot.accept("./components/App", () => {
    const NextApp = (require("./components/App") as { default: typeof App }).default;
    root?.render(
      <ErrorBoundary area="taskpane-root" title="Quillio couldn't start">
        <AppProviders>
          <NextApp />
        </AppProviders>
      </ErrorBoundary>
    );
  });
}
